import React, { useEffect, useState, useCallback } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Button,
  CircularProgress,
  Typography,
  TextField,
  Link,
} from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import axiosInstance from "../../../utils/config/config";
import { colors, fonts } from "../../../theme/theme";

// Render a single value for the diff: arrays as comma lists, file URLs as links,
// empty as an em dash.
const isUrl = (v) => typeof v === "string" && /^https?:\/\//i.test(v);
const renderValue = (v) => {
  if (v == null || v === "" || (Array.isArray(v) && v.length === 0)) {
    return <span style={{ color: colors.textLight }}>—</span>;
  }
  if (Array.isArray(v)) {
    if (v.every(isUrl)) {
      return v.map((u, i) => (
        <Link key={i} href={u} target="_blank" rel="noopener" sx={{ fontSize: 13, mr: 1 }}>
          file {v.length > 1 ? i + 1 : ""}
        </Link>
      ));
    }
    return v.join(", ");
  }
  if (isUrl(v)) {
    return (
      <Link href={v} target="_blank" rel="noopener" sx={{ fontSize: 13 }}>
        {v.length > 60 ? "view file / link" : v}
      </Link>
    );
  }
  if (typeof v === "boolean") return v ? "Yes" : "No";
  return String(v);
};

const ReviewChangesModal = ({ practitionerId, open, onClose, onPublish, onReject }) => {
  const [loading, setLoading] = useState(true);
  const [review, setReview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [rejectMode, setRejectMode] = useState(false);
  const [comment, setComment] = useState("");
  const [error, setError] = useState("");

  const reset = useCallback(() => {
    setRejectMode(false);
    setComment("");
    setError("");
  }, []);

  useEffect(() => {
    if (!open || !practitionerId) return;
    let cancelled = false;
    reset();
    (async () => {
      setLoading(true);
      try {
        const { data } = await axiosInstance.get(`practitioners/${practitionerId}/pending-edit`);
        if (!cancelled) setReview(data);
      } catch (err) {
        if (!cancelled) setReview(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, practitionerId, reset]);

  const doPublish = async () => {
    setBusy(true);
    const ok = await onPublish(practitionerId);
    setBusy(false);
    if (ok) onClose();
  };

  const doReject = async () => {
    if (!comment.trim()) {
      setError("Please add a note so the practitioner knows what to fix.");
      return;
    }
    setBusy(true);
    const ok = await onReject(practitionerId, comment.trim());
    setBusy(false);
    if (ok) onClose();
  };

  const changes = review?.changedFields || [];
  // A review is actionable only while still pending. Once published/rejected it
  // is terminal — show the outcome read-only, no re-answering.
  const isPending = review?.status === "pending";

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ fontFamily: fonts.gotham, color: colors.primary }}>
        Review Profile Changes
      </DialogTitle>
      <DialogContent dividers>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
            <CircularProgress sx={{ color: colors.secondary }} />
          </Box>
        ) : !review ? (
          <Typography sx={{ fontFamily: fonts.quicksand, color: colors.textLight, py: 4, textAlign: "center" }}>
            No pending changes to review.
          </Typography>
        ) : changes.length === 0 ? (
          <Typography sx={{ fontFamily: fonts.quicksand, color: colors.textLight, py: 4, textAlign: "center" }}>
            The submission contains no changes versus the published profile.
          </Typography>
        ) : (
          <>
            {!isPending && (
              <Box
                sx={{
                  mb: 2,
                  p: 1.5,
                  borderRadius: 2,
                  fontFamily: fonts.quicksand,
                  fontSize: 14,
                  backgroundColor: review.status === "approved" ? "#E7F4E8" : "#FBE7E7",
                  color: review.status === "approved" ? "#2f7d4f" : "#C0392B",
                  fontWeight: 600,
                }}
              >
                {review.status === "approved"
                  ? "These changes were already published — no further action needed."
                  : "These changes were rejected."}
                {review.status === "rejected" && review.reviewComment
                  ? ` Note: ${review.reviewComment}`
                  : ""}
              </Box>
            )}
            <Typography sx={{ fontFamily: fonts.quicksand, fontSize: 14, color: colors.textLight, mb: 2 }}>
              {changes.length} field{changes.length > 1 ? "s" : ""} changed
              {isPending && review.submittedByRole === "admin" ? " (your edit — review before publishing)" : ""}.
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              {/* header row */}
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "1.2fr 1.6fr 1.6fr",
                  gap: 1.5,
                  px: 1,
                  fontFamily: fonts.quicksand,
                  fontSize: 12,
                  fontWeight: 700,
                  letterSpacing: "0.5px",
                  textTransform: "uppercase",
                  color: colors.textLight,
                }}
              >
                <span>Field</span>
                <span>Before</span>
                <span>After</span>
              </Box>
              {changes.map((c) => (
                <Box
                  key={c.field}
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "1.2fr 1.6fr 1.6fr",
                    gap: 1.5,
                    p: 1,
                    borderRadius: 2,
                    backgroundColor: colors.headerBg,
                    fontFamily: fonts.quicksand,
                    fontSize: 14,
                    alignItems: "start",
                  }}
                >
                  <Typography sx={{ fontWeight: 600, color: colors.primary, fontFamily: fonts.quicksand, fontSize: 14 }}>
                    {c.label}
                  </Typography>
                  <Box sx={{ color: colors.text, wordBreak: "break-word" }}>{renderValue(c.before)}</Box>
                  <Box sx={{ color: colors.text, wordBreak: "break-word", fontWeight: 600 }}>
                    {renderValue(c.after)}
                  </Box>
                </Box>
              ))}
            </Box>

            {rejectMode && (
              <Box sx={{ mt: 3 }}>
                <Typography sx={{ fontFamily: fonts.quicksand, fontWeight: 600, mb: 1, color: colors.primary }}>
                  Rejection note (shown to the practitioner)
                </Typography>
                <TextField
                  fullWidth
                  multiline
                  minRows={2}
                  value={comment}
                  onChange={(e) => {
                    setComment(e.target.value);
                    setError("");
                  }}
                  placeholder="Explain what needs to be corrected..."
                  sx={{ fontFamily: fonts.quicksand }}
                />
                {error && (
                  <Typography sx={{ color: "#C0392B", fontFamily: fonts.quicksand, fontSize: 13, mt: 0.5 }}>
                    {error}
                  </Typography>
                )}
              </Box>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2, gap: 1, justifyContent: "space-between", flexWrap: "wrap" }}>
        <Button
          onClick={onClose}
          disabled={busy}
          variant="outlined"
          sx={{ textTransform: "none", fontFamily: fonts.quicksand, borderColor: colors.lightBorder, color: colors.text }}
        >
          Close
        </Button>
        {review && isPending && changes.length > 0 && (
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
            {rejectMode ? (
              <>
                <Button
                  onClick={() => {
                    setRejectMode(false);
                    setError("");
                  }}
                  disabled={busy}
                  sx={{ textTransform: "none", fontFamily: fonts.quicksand, color: colors.text }}
                >
                  Back
                </Button>
                <Button
                  onClick={doReject}
                  disabled={busy}
                  variant="contained"
                  startIcon={<HighlightOffIcon />}
                  sx={{ textTransform: "none", fontFamily: fonts.quicksand, backgroundColor: "#C0392B", "&:hover": { backgroundColor: "#a53125" } }}
                >
                  {busy ? "Rejecting..." : "Confirm Reject"}
                </Button>
              </>
            ) : (
              <>
                <Button
                  onClick={() => setRejectMode(true)}
                  disabled={busy}
                  startIcon={<HighlightOffIcon />}
                  sx={{ textTransform: "none", fontFamily: fonts.quicksand, color: "#C0392B" }}
                >
                  Reject
                </Button>
                <Button
                  onClick={doPublish}
                  disabled={busy}
                  variant="contained"
                  startIcon={<CheckCircleOutlineIcon />}
                  sx={{ textTransform: "none", fontFamily: fonts.quicksand, backgroundColor: colors.secondary, "&:hover": { backgroundColor: "#1f2e3e" } }}
                >
                  {busy ? "Publishing..." : "Publish Changes"}
                </Button>
              </>
            )}
          </Box>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ReviewChangesModal;
